import { required, createI18nMessage } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { isObject, isString } from '@aviata/type-guards';
import type { MessageProps } from '@vuelidate/validators';

type ValidatorWithMessagePath = MessageProps & {
  $response: {
    messagePath: string;
  };
};

const hasMessagePath = (
  validator: MessageProps,
): validator is ValidatorWithMessagePath =>
  isObject(validator.$response) && isString(validator.$response.messagePath);

/**
 * Sometimes we need a few different validation messages for the same validator.
 * To achieve this we need to pass messagePath extra value with a suffix to message.
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#returning-extra-data-from-validators
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#customising-the-i18n-message
 */
const getMessagePath = (messageRootPath: string) => {
  return (validator: MessageProps) => {
    const basePath = `${messageRootPath}.${validator.$validator}`;
    return hasMessagePath(validator)
      ? `${basePath}.${validator.$response.messagePath}`
      : basePath;
  };
};

/**
 * Method for localization of validation messages.
 * Validation name and message path in locales should match exactly.
 * Otherwise, validator will not have message
 *
 * @param messageRootPath - root path to validation locales
 */
const useLocalizedValidators = (messageRootPath: string) => {
  const { t } = useI18n();

  /**
   * Method maps a validator name with `$response.messagePath` to its translation in locales.
   * All validations must be in locales file under **messageRootPath**
   *
   * @example
   *
   * ru.json
   * ```json
   * {
   *   "booking": {
   *     "validations": {
   *       "required": "Обязательное поле",
   *       "documentNumber": {
   *         "id": "Номер удостоверения",
   *         "passport": "Паспорт"
   *       }
   *     }
   *   }
   * }
   * ```
   *
   * ```
   * In this case 'messageRootPath' is 'booking.validations'
   * ```
   *
   * @see https://vuelidate-next.netlify.app/advanced_usage.html#i18n-support
   */
  const withI18nMessage = createI18nMessage({
    t,
    messagePath: getMessagePath(messageRootPath),
  });
  const localizedRequired = withI18nMessage(required);

  return {
    required: localizedRequired,
    withI18nMessage,
  };
};

export const validationI18n = {
  model: {
    useLocalizedValidators,
  },
};
